import React from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import styled from 'styled-components';
import { Container, Button, Row, Column } from '../design-system';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '../design-system';
import { useToggle } from 'react-use';
import FieldColumn from './FieldColumn';
import { COUNTRY_OPTIONS, STATE_OPTIONS } from '../globalConstants';
import DateField from './DateField';

// ModalContent scrolls when a form is taller than the dialog, so the action row
// sticks to the bottom of that scroll area and stays on screen instead of being
// pushed below the fold. Sticky is inert when the form already fits.
//
// `bottom: 0` pins the row to the bottom of ModalContent's padding box, so the
// row supplies its own bottom padding and an opaque background for the fields
// scrolling underneath it.
const ActionRow = styled.div`
  position: sticky;
  bottom: 0;
  background: #ffffff;
  padding-top: 16px;
  padding-bottom: 20px;
`;

interface ModalFormField {
  name: string;
  label?: string;
  type?: string;
  [key: string]: any;
}

interface ModalFormProps {
  title?: string;
  fields?: ModalFormField[];
  fieldWidth?: any;
  isOpen: boolean;
  onClose: () => void;
  oneColumn?: boolean;
  onSubmit: (values: any) => any;
  onChange?: (values: any) => any;
  render?: (values: any) => React.ReactNode;
  initialValues?: Record<string, any>;
  buttonText?: string;
  isConfirmation?: boolean;
  id?: string;
  modalWidth?: number;
  [key: string]: any;
}

const ModalForm = ({
  title,
  fields = [],
  fieldWidth,
  isOpen,
  onClose,
  oneColumn,
  onSubmit,
  render,
  initialValues,
  buttonText,
  isConfirmation = true,
}: ModalFormProps) => {
  const [isOpenConfirmation, toggleConfirmation] = useToggle(false);
  const [isDirty, toggleDirty] = useToggle(false);
  const [showingPassword, showPassword] = useToggle(false);

  const handleCancel = (form: any) => {
    form.reset();
    onClose();
  };

  const handleClose = () => {
    if (isDirty && isConfirmation) {
      toggleConfirmation(true);
    } else {
      onClose();
    }
  };

  const displayText = () => {
    let displayVal;
    if (buttonText) {
      displayVal = buttonText;
    } else {
      displayVal = initialValues ? 'Update' : 'Add';
    }
    return displayVal;
  };

  const showPasswordField = () => {
    showPassword(true);
  };

  // Returning the promise lets final-form keep `submitting` true for the
  // whole request and drop re-entrant submits itself.
  const submit = (values: any, form: any) =>
    Promise.resolve(onSubmit(values)).then(() => {
      form.reset();
    });

  return (
    <>
      <Modal
        id="active-modal"
        show={isOpen}
        onClose={handleClose}
        style={{ backgroundColor: 'white' }}
      >
        <ModalHeader>{title}</ModalHeader>
        <ModalContent>
          <Form
            initialValues={initialValues || {}}
            onSubmit={submit}
            render={({ handleSubmit, form, submitting, valid, values }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <FormSpy
                    subscription={{ dirty: true }}
                    onChange={({ dirty }) => toggleDirty(dirty)}
                  />
                  {render ? (
                    render(values)
                  ) : (
                    <Container>
                      {!oneColumn ? (
                        <Row>
                          {fields.map((field) => {
                            if (
                              field.type === 'date' ||
                              field.type === 'Date'
                            ) {
                              return (
                                <Column key={field.name} pb={2} width={300}>
                                  <Field name={field.name}>
                                    {({ input }) => {
                                      return (
                                        <DateField
                                          name={field.name}
                                          label={field.label}
                                          selected={input.value}
                                          onChange={(value) =>
                                            input.onChange(value)
                                          }
                                          showPastDue={false}
                                        />
                                      );
                                    }}
                                  </Field>
                                </Column>
                              );
                            }

                            return (
                              <FieldColumn
                                key={field.name}
                                width={fieldWidth}
                                {...field}
                                {...(field.name === 'state' && {
                                  options: values.country
                                    ? STATE_OPTIONS(values.country)
                                    : [],
                                })}
                                {...(field.name === 'country' && {
                                  options: COUNTRY_OPTIONS,
                                  onChange: () => {
                                    form.batch(() => {
                                      form.change('state', null);
                                    });
                                  },
                                })}
                              />
                            );
                          })}
                        </Row>
                      ) : (
                        fields.map((field) => {
                          if (field.type === 'date' || field.type === 'Date') {
                            return (
                              <Column key={field.name} pb={2} width={300}>
                                <Field name={field.name}>
                                  {({ input }) => {
                                    return (
                                      <DateField
                                        name={field.name}
                                        label={field.label}
                                        selected={input.value}
                                        onChange={(value) =>
                                          input.onChange(value)
                                        }
                                        showPastDue={false}
                                      />
                                    );
                                  }}
                                </Field>
                              </Column>
                            );
                          }

                          return (
                            <div key={field.name}>
                              {(() => {
                                if (field.name === 'password') {
                                  return (
                                    <div
                                      id="manual-password"
                                      style={{ display: 'none' }}
                                    >
                                      <FieldColumn
                                        width={fieldWidth}
                                        {...field}
                                      />
                                    </div>
                                  );
                                }

                                if (field.name === 'setPassword') {
                                  return (
                                    <div>
                                      <FieldColumn
                                        width={fieldWidth}
                                        onChange={() => {
                                          const x =
                                            document.getElementById(
                                              'manual-password',
                                            );
                                          if (x) {
                                            if (x.style.display === 'none') {
                                              x.style.display = 'block';
                                            } else {
                                              x.style.display = 'none';
                                            }
                                          }
                                        }}
                                        {...field}
                                      />
                                    </div>
                                  );
                                }

                                if (field.name === 'clients') {
                                  const fieldName: string = field.name;
                                  return (
                                    <div>
                                      {values.isClient && (
                                        <FieldColumn
                                          width={fieldWidth}
                                          {...field}
                                          {...(fieldName === 'state' && {
                                            options: values.country
                                              ? STATE_OPTIONS(values.country)
                                              : [],
                                          })}
                                          {...(fieldName === 'country' && {
                                            options: COUNTRY_OPTIONS,
                                          })}
                                        />
                                      )}
                                    </div>
                                  );
                                }

                                return (
                                  <FieldColumn
                                    width={fieldWidth}
                                    {...field}
                                    {...(field.name === 'state' && {
                                      options: values.country
                                        ? STATE_OPTIONS(values.country)
                                        : [],
                                    })}
                                    {...(field.name === 'country' && {
                                      options: COUNTRY_OPTIONS,
                                    })}
                                    {...(field.name ===
                                      'notification_target' && {
                                      value: initialValues
                                        ? initialValues.assignee_id
                                        : null,
                                    })}
                                  />
                                );
                              })()}
                            </div>
                          );
                        })
                      )}
                    </Container>
                  )}
                  <ActionRow data-testid="modal-form__actions">
                    <Button.Group justifyContent="space-between">
                      <Button
                        id="modal-cancel-button"
                        type="button"
                        onClick={() => handleCancel(form)}
                        variant="grey"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        id="modal-submit-button"
                        variant="primary"
                        disabled={!valid || submitting}
                        loading={submitting}
                      >
                        {displayText()}
                      </Button>
                    </Button.Group>
                  </ActionRow>
                </form>
              );
            }}
          />
        </ModalContent>
      </Modal>
      <Modal show={isOpenConfirmation} onClose={toggleConfirmation}>
        <ModalContent>
          <p>Your changes will be lost unless you save them.</p>
        </ModalContent>
        <ModalFooter>
          <Button.Group alignItems="center" justifyContent="flex-end">
            <Button
              variant="primaryText"
              size="xs"
              onClick={() => {
                onClose();
                toggleConfirmation();
              }}
            >
              Close Without Saving
            </Button>
            <Button
              onClick={() => {
                toggleConfirmation();
              }}
            >
              Continue Editing
            </Button>
          </Button.Group>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ModalForm;
